// 共享 Observer 實例池（按 options key 分組）
let observerPool = null;

// 確保只在客戶端初始化
const getObserverPool = () => {
    if (!observerPool) {
        observerPool = new Map();
    }
    return observerPool;
};

/**
 * 共享 IntersectionObserver 管理器
 * 將相同 options 的 Observer 合併為單一實例，減少記憶體消耗與主線程負擔
 * 
 * @param {Object} options - IntersectionObserver 選項
 * @param {number|number[]} options.threshold - 可見度閾值
 * @param {string} options.rootMargin - 根元素邊距
 * @returns {{ observe: Function, unobserve: Function }}
 */
export function useSharedIntersectionObserver(options = {}) {
    /**
     * 註冊元素觀察
     * @param {Element} el - 要觀察的元素
     * @param {Function} callback - 可見性變化時的回調函數
     */
    const observe = (el, callback) => {
        // SSR 保護：只在客戶端執行
        if (typeof window === "undefined" || !el) return;

        const pool = getObserverPool();

        // 根據 options 生成唯一 key
        const optionsKey = JSON.stringify({
            threshold: options.threshold ?? 0,
            rootMargin: options.rootMargin ?? "0px",
        });

        // 取得或創建共享 Observer
        if (!pool.has(optionsKey)) {
            const callbacks = new Map();
            const observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    const cb = callbacks.get(entry.target);
                    cb?.(entry);
                });
            }, options);

            pool.set(optionsKey, { observer, callbacks });
        }

        const { observer, callbacks } = pool.get(optionsKey);
        callbacks.set(el, callback);
        observer.observe(el);
    };

    /**
     * 取消元素觀察
     * @param {Element} el - 要取消觀察的元素
     */
    const unobserve = (el) => {
        // SSR 保護：只在客戶端執行
        if (typeof window === "undefined" || !el) return;

        const pool = getObserverPool();

        // 根據 options 生成唯一 key
        const optionsKey = JSON.stringify({
            threshold: options.threshold ?? 0,
            rootMargin: options.rootMargin ?? "0px",
        });

        const entry = pool.get(optionsKey);
        if (entry) {
            entry.callbacks.delete(el);
            entry.observer.unobserve(el);
        }
    };

    return { observe, unobserve };
}
